/**
 * AlertNotification - Bell icon with notification count and alert panel
 */

import { useState, useEffect, useRef } from 'react';
import {
  Alert,
  AlertType,
  AlertSeverity,
  
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  dismissAlert,
  subscribeToAlerts,
} from '../services/AlertService';
import './AlertNotification.css';

export default function AlertNotification() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<AlertType | 'all'>('all');
  const panelRef = useRef<HTMLDivElement>(null);

  // Subscribe to alert updates
  useEffect(() => {
    const unsubscribe = subscribeToAlerts((newAlerts) => {
      setAlerts(newAlerts);
      setUnreadCount(getUnreadCount());
    });
    return unsubscribe;
  }, []);

  // Close panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get filtered alerts
  const filteredAlerts = filter === 'all' 
    ? alerts 
    : alerts.filter(a => a.type === filter);

  // Handle alert click
  const handleAlertClick = (alert: Alert) => {
    if (!alert.read) {
      markAsRead(alert.id);
    }
  };

  return (
    <div className="alert-notification" ref={panelRef}>
      {/* Bell Icon */}
      <button 
        className={`alert-bell ${unreadCount > 0 ? 'has-alerts' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title={`${unreadCount} unread alerts`}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="alert-count">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {/* Alert Panel */}
      {isOpen && (
        <div className="alert-panel">
          <div className="alert-panel-header">
            <h3>Alerts</h3>
            <div className="alert-header-actions">
              {unreadCount > 0 && (
                <button 
                  className="mark-read-btn"
                  onClick={markAllAsRead}
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="alert-filters">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button 
              className={`filter-btn ${filter === 'circuit' ? 'active' : ''}`}
              onClick={() => setFilter('circuit')}
            >
              Circuits
            </button>
            <button 
              className={`filter-btn ${filter === 'node' ? 'active' : ''}`}
              onClick={() => setFilter('node')}
            >
              Nodes
            </button>
            <button 
              className={`filter-btn ${filter === 'version' ? 'active' : ''}`}
              onClick={() => setFilter('version')}
            >
              Version
            </button>
          </div>

          {/* Alert List */}
          <div className="alert-list">
            {filteredAlerts.length === 0 ? (
              <div className="alert-empty">
                <span className="empty-icon">🔔</span>
                <p>No alerts</p>
              </div>
            ) : (
              filteredAlerts.map(alert => (
                <AlertItem 
                  key={alert.id} 
                  alert={alert}
                  onClick={() => handleAlertClick(alert)}
                  onDismiss={() => dismissAlert(alert.id)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Alert Item Component
interface AlertItemProps {
  alert: Alert;
  onClick: () => void;
  onDismiss: () => void;
}

function AlertItem({ alert, onClick, onDismiss }: AlertItemProps) {
  const timeAgo = formatTimeAgo(alert.timestamp);
  
  return (
    <div 
      className={`alert-item ${!alert.read ? 'unread' : ''} severity-${alert.severity}`}
      onClick={onClick}
    >
      <div className="alert-item-icon">
        <SeverityIcon severity={alert.severity} type={alert.type} />
      </div>
      <div className="alert-item-content">
        <div className="alert-item-header">
          <span className="alert-title">{alert.title}</span>
          <span className="alert-time">{timeAgo}</span>
        </div>
        <p className="alert-message">{alert.message}</p>
        <span className={`alert-type-badge type-${alert.type}`}>{alert.type}</span>
      </div>
      <button 
        className="alert-dismiss"
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

// Severity Icon Component
function SeverityIcon({ severity, type }: { severity: AlertSeverity; type: AlertType }) {
  if (type === 'node') {
    return <span>🖥️</span>;
  }
  if (type === 'circuit') {
    return <span>⚡</span>;
  }
  if (type === 'version') {
    return <span>📦</span>;
  }
  
  switch (severity) {
    case 'critical':
      return <span>🔴</span>;
    case 'warning':
      return <span>🟠</span>;
    default:
      return <span>🔵</span>;
  }
}

// Bell Icon SVG
function BellIcon() {
  return (
    <svg 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2"
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

// Format time ago helper
function formatTimeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'Just now';
}
